/**
 * O token da sessão do portal (RN-POR-03): fica no `localStorage` deste aparelho, sob
 * `wx-portal-token`, e vai em `Authorization: Bearer` em toda chamada. Quem o apaga diz por quê:
 * "Sair" ou a API dizendo que a sessão terminou (401), e a tela de entrada mostra o motivo.
 */

export const TOKEN_KEY = 'wx-portal-token'

export type ClearReason = 'expired' | 'signed_out'

export interface TokenStore {
  get(): string | null
  set(token: string): void
  clear(reason: ClearReason): void
  /** Por que o token foi apagado por último; `null` enquanto ninguém apagou nesta aba. */
  reason(): ClearReason | null
  subscribe(listener: () => void): () => void
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function createTokenStore(storage: Storage | null = safeStorage()): TokenStore {
  const listeners = new Set<() => void>()
  let cached: string | null = null
  let lastReason: ClearReason | null = null
  try {
    cached = storage?.getItem(TOKEN_KEY) ?? null
  } catch {
    cached = null
  }
  const notify = () => listeners.forEach((listener) => listener())
  return {
    get: () => cached,
    set(token) {
      cached = token
      lastReason = null
      try {
        storage?.setItem(TOKEN_KEY, token)
      } catch {
        // Sem armazenamento, a sessão vive só nesta aba.
      }
      notify()
    },
    clear(reason) {
      // Apagar o que já não existe não muda o motivo: a API pode responder 401 mais de uma vez.
      if (cached === null && lastReason !== null) return
      cached = null
      lastReason = reason
      try {
        storage?.removeItem(TOKEN_KEY)
      } catch {
        // idem
      }
      notify()
    },
    reason: () => lastReason,
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
